import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalDialog } from 'components/ModalDialog';
import { MODAL_DIALOG_WIDTH, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { IMenuLink } from 'modules/core/components/SideBar/sideBarMenuLinks';

import {
  ChevronIcon,
  ContentPanel,
  DialogBody,
  DialogContainer,
  DialogContent,
  DialogHeader,
  HeaderLogo,
  HeaderTitle,
  MakeItWorkText,
  MODAL_DIALOG_STYLE,
  OptionCard,
  OptionGrid,
  OptionLabel,
  Section,
  SectionTitle,
  SELECT_LENDER_BACKDROP_CLASS,
  SelectLenderGlobalStyles,
} from './styles';

interface ISelectLenderDialog {
  show: boolean;
  lenders: IMenuLink[];
  secondaryLinks?: IMenuLink[];
  onSelect: (lenderPath: string) => void;
}

export const SelectLenderDialog: FC<ISelectLenderDialog> = ({ show, lenders, secondaryLinks = [], onSelect }) => {
  const { t } = useTranslation();

  return (
    <>
      <SelectLenderGlobalStyles />
      <ModalDialog
        show={show}
        animation={false}
        container={document.body}
        modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_750}
        modalStyle={MODAL_DIALOG_STYLE}
        backdropClassName={SELECT_LENDER_BACKDROP_CLASS}
        backdrop="static"
        keyboard={false}
        showCloseButton={false}
        showFooter={false}
        showHeader={false}
      >
        <DialogContainer>
          <DialogHeader>
            <HeaderLogo src={STATIC_IMAGE_URLS.logoDark} alt="Streamline" />
            <HeaderTitle>{t('components.selectLender.headerTitle')}</HeaderTitle>
          </DialogHeader>

          <DialogBody>
            <ContentPanel>
              <DialogContent>
                {lenders.length > 0 && (
                  <Section>
                    <SectionTitle>
                      {t('components.selectLender.lendersSectionTitlePrefix')}
                      <MakeItWorkText>{t('components.selectLender.makeItWork')}</MakeItWorkText>
                    </SectionTitle>
                    <OptionGrid>
                      {lenders.map((lender) => {
                        const lenderPath = lender.link as string;

                        return (
                          <OptionCard
                            key={lenderPath}
                            type="button"
                            onClick={() => onSelect(lenderPath)}
                            data-testid={`select-lender-${lender.name}`}
                          >
                            <OptionLabel>{lender.uiName || lender.name}</OptionLabel>
                            <ChevronIcon aria-hidden>&gt;</ChevronIcon>
                          </OptionCard>
                        );
                      })}
                    </OptionGrid>
                  </Section>
                )}

                {secondaryLinks.length > 0 && (
                  <Section>
                    <SectionTitle>
                      <MakeItWorkText>{t('components.selectLender.makeItWork')}</MakeItWorkText>
                      {t('components.selectLender.allLendersSectionTitleSuffix')}
                    </SectionTitle>
                    <OptionGrid>
                      {secondaryLinks.map((link) => {
                        const linkPath = link.link as string;

                        return (
                          <OptionCard
                            key={linkPath}
                            type="button"
                            onClick={() => onSelect(linkPath)}
                            data-testid={`select-lender-secondary-${link.name}`}
                          >
                            <OptionLabel>{link.uiName || link.name}</OptionLabel>
                            <ChevronIcon aria-hidden>&gt;</ChevronIcon>
                          </OptionCard>
                        );
                      })}
                    </OptionGrid>
                  </Section>
                )}
              </DialogContent>
            </ContentPanel>
          </DialogBody>
        </DialogContainer>
      </ModalDialog>
    </>
  );
};
